import { CRUDSliceType } from '@redux/crudCreator/types';
import { RootState } from '@redux/store';
import { createDraftSafeSelector } from '@reduxjs/toolkit';
import { TFilterRequest } from 'components/rest/Filter/FilterContent';
import _, { get, zipObjectDeep } from 'lodash';
import { getFilterFromUrl } from 'utils/tools';

export class CRUDSelectors {
  resource: string | undefined;
  constructor(resource) {
    this.resource = resource;
  }

  getRestData = (state: RootState): CRUDSliceType => state[this.resource];
  getRestDataIds = (state: RootState): string[] => state[this.resource].ids;
  getRestDataTotal = (state: RootState): number => state[this.resource].total;
  getRestDataFilters = (state: RootState): TFilterRequest =>
    state[this.resource].filters;
  getRestDataPage = (state: RootState): number => state[this.resource].page;
  getRestDataPageSize = (state: RootState): number =>
    state[this.resource].pageSize;
  getRestDataOrderBy = (state: RootState): string =>
    state[this.resource].orderBy;
  getRestDataQ = (state: RootState): string => state[this.resource].q;
  getRestDataLoading = (state: RootState): string =>
    state[this.resource].loading;
  getRestDataItemLoadings = (state: RootState): string =>
    state[this.resource].itemLoadings;

  getColumnData = (state: RootState, props): CRUDSliceType =>
    get(state[this.resource].data[props.id], props.name);

  getDefaultValue = (state, props) =>
    props.location && (!props.location.hash || props.location.hash === '#')
      ? decodeURI(props.location.search.substring(1)).trim()
      : props.location.hash.match(`#${this.resource}/create?(.*)`)?.[1];

  getDefaultFromProps = (state, props) => props.defaultValue;

  getDataArr = createDraftSafeSelector([this.getRestDataIds], (ids = []) => {
    return ids?.map((id) => ({ id })) || [];
  });

  getTotal = createDraftSafeSelector([this.getRestDataTotal], (total) => {
    return total;
  });

  getDefaultCreateData = createDraftSafeSelector(
    [this.getDefaultValue, this.getDefaultFromProps],
    (defaultValue, defaultValueFromProps) =>
      defaultValue !== ''
        ? zipObjectDeep(
            Object.keys(getFilterFromUrl(defaultValue).filter),
            _.values(getFilterFromUrl(defaultValue).filter),
          )
        : defaultValueFromProps || {},
  );

  getCurrentData = createDraftSafeSelector(
    [this.getRestData],
    (resources: CRUDSliceType) => {
      const { currentData } = resources;
      return currentData || {};
    },
  );

  getLoadingCurrentRecord = createDraftSafeSelector(
    [this.getRestData],
    (resources: CRUDSliceType) => {
      const { currentId, itemLoadings } = resources;
      return itemLoadings[currentId];
    },
  );

  enabledLoadMore = createDraftSafeSelector([this.getRestData], (resources) => {
    const { offset, limit, loading, numberOfPages } = resources;
    return !loading && offset / limit + 1 < numberOfPages;
  });

  getLoading = createDraftSafeSelector([this.getRestDataLoading], (loading) => {
    return loading;
  });

  getCreateLoading = createDraftSafeSelector(
    [this.getRestData],
    (resources: CRUDSliceType) => {
      const { createLoading } = resources;
      return createLoading;
    },
  );

  getError = createDraftSafeSelector([this.getRestData], (resources) => {
    const { error } = resources;
    return error;
  });

  getFilters = createDraftSafeSelector(
    [
      this.getRestDataFilters,
      this.getTotal,
      this.getRestDataOrderBy,
      this.getRestDataPage,
      this.getRestDataPageSize,
      this.getRestDataQ,
    ],
    (filters, total, orderBy, page, pageSize, q) => {
      return { page, pageSize, filters, count: total, orderBy, q };
    },
  );

  getLoadingById = createDraftSafeSelector(
    [this.getRestData],
    (resources: CRUDSliceType) => {
      const { currentId, itemLoadings } = resources;
      return { [currentId]: itemLoadings[currentId] };
    },
  );

  getDataRecordArr = createDraftSafeSelector(
    [this.getRestData],
    (resources) => {
      const { data, ids } = resources;
      return ids?.map((id) => data[id]);
    },
  );

  getSelectedRowKeys = createDraftSafeSelector(
    [this.getRestData],
    (resources: any) => {
      const { selectedRowKeys } = resources;
      return selectedRowKeys || [];
    },
  );
}

export const crudSelectors = new CRUDSelectors(undefined);
